import React, { useState, useEffect, useCallback } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import CircularProgress from '@mui/material/CircularProgress';
import { getArchive, reopenTask } from '@/services/taskService';

const UNASSIGNED = '#9CA3AF';

/** Completed-task archive with staff + date-range filters. */
const ArchiveModal = ({ staff, onClose, onChanged }) => {
  const [tasks, setTasks]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignee, setAssignee] = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  const [restoring, setRestoring] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getArchive({ assigned_to: assignee || undefined, from: from || undefined, to: to || undefined });
      setTasks(res?.tasks || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [assignee, from, to]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (id) => {
    setRestoring(id);
    try { await reopenTask(id); setTasks((t) => t.filter((x) => x.id !== id)); onChanged && onChanged(); }
    catch (e) { console.error(e); }
    setRestoring(null);
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 bg-gray-700 rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-2">
            <ArchiveOutlinedIcon style={{ fontSize: 20, color: '#fff' }} />
            <h2 className="text-base font-bold text-white">Completed Tasks Archive</h2>
            {!loading && <span className="text-xs bg-white/20 text-white rounded-full px-2 py-0.5">{tasks.length}</span>}
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white transition"><CloseIcon /></button>
        </div>

        {/* Filters */}
        <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap flex-shrink-0">
          <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
            className="py-1.5 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white">
            <option value="">All staff</option>
            {staff.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
          </select>
          <label className="text-xs text-gray-500">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <label className="text-xs text-gray-500">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="py-1.5 px-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300" />
          {(assignee || from || to) && (
            <button onClick={() => { setAssignee(''); setFrom(''); setTo(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline">Clear</button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex justify-center py-10"><CircularProgress size={24} style={{ color: '#6b7280' }} /></div>
          ) : tasks.length === 0 ? (
            <p className="text-center text-sm text-gray-400 py-10 italic">No completed tasks match these filters.</p>
          ) : tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50 hover:bg-white transition group">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: t.assigned_to_color || UNASSIGNED }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-700 line-through truncate">{t.title}</p>
                <p className="text-[11px] text-gray-400">
                  {t.assigned_to_name || 'Unassigned'} · due {fmtDate(t.due_date)} · completed {fmtDate(t.completed_at)}
                </p>
              </div>
              <button onClick={() => handleRestore(t.id)} disabled={restoring === t.id}
                className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:text-blue-700 disabled:opacity-40 transition">
                <RestoreIcon style={{ fontSize: 14 }} />
                {restoring === t.id ? '…' : 'Reopen'}
              </button>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 flex-shrink-0">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition">Close</button>
        </div>
      </div>
    </div>
  );
};

export default ArchiveModal;
